import React, { useState } from 'react';
import './chatbot.scss';

interface SecondaryMatch {
  label: string;
  collection: string;
  filter?: any;
  id?: string;
}

interface SecondaryFilterSelectionProps {
  matches: SecondaryMatch[];
  onApply: (selectedFilters: SecondaryMatch[]) => void;
  onDiscard: () => void;
  hideCollection?: boolean;
  selectedOptions?: string[];
  keepOpen?: boolean;
}

const SecondaryFilterSelection: React.FC<SecondaryFilterSelectionProps> = ({
  matches,
  onApply,
  onDiscard,
  hideCollection = false,
  selectedOptions = [],
  keepOpen = false
}) => {
  // Always call hooks at the top level
  const [selectedMatches, setSelectedMatches] = useState<Set<string>>(new Set());

  // If hideCollection is true (track name selection), clicking a match applies it instantly
  if (hideCollection) {
    return (
      <div className="secondary-filter-selection">
        {matches.map((match) => {
          const isSelected = selectedOptions.includes(match.label);
          return (
            <div
              key={match.id || match.label}
              className="filter-option"
              style={{ cursor: isSelected ? 'not-allowed' : 'pointer', background: isSelected ? 'rgba(107, 107, 255, 0.18)' : undefined, opacity: isSelected ? 0.6 : 1 }}
              onClick={() => {
                if (!isSelected) onApply([match]);
              }}
            >
              <span className="option-text">{match.label}</span>
              {isSelected && <span style={{ marginLeft: 8, color: '#6b6bff', fontWeight: 700 }}>&#10003;</span>}
            </div>
          );
        })}
        {!keepOpen && (
          <div className="action-buttons">
            <button className="discard-button" onClick={onDiscard}>Close</button>
          </div>
        )}
      </div>
    );
  }

  const getMatchId = (match: SecondaryMatch) => `${match.label}-${match.collection}`;

  const handleCheckboxChange = (match: SecondaryMatch) => {
    const matchId = getMatchId(match);
    const newSelected = new Set(selectedMatches);
    if (newSelected.has(matchId)) {
      newSelected.delete(matchId);
    } else {
      newSelected.add(matchId);
    }
    setSelectedMatches(newSelected);
  };

  const handleApply = () => {
    const selectedFilters = matches.filter(match => 
      selectedMatches.has(getMatchId(match))
    );
    onApply(selectedFilters);
  };

  return (
    <div className="secondary-filter-selection">
      {matches.map((match) => (
        <label key={getMatchId(match)} className="filter-option">
          <input
            type="checkbox"
            checked={selectedMatches.has(getMatchId(match))}
            onChange={() => handleCheckboxChange(match)}
          />
          <span className="option-text">
            {hideCollection ? match.label : `${match.label} - ${match.collection}`}
          </span>
        </label>
      ))}
      <div className="action-buttons">
        <button 
          className="apply-button"
          onClick={handleApply}
          disabled={selectedMatches.size === 0}
        >
          Apply Selected
        </button>
        <button 
          className="discard-button"
          onClick={onDiscard}
        >
          Discard
        </button>
      </div>
    </div>
  );
};

export default SecondaryFilterSelection; 